<script lang="ts" setup>
import { computed, ref } from "vue";
import type { Component } from "vue";
import { IComponentInputEmits, IComponentSearchProps } from "@/types/main";
import { vMaska } from "maska/vue";
import { X } from "utruckin-icons";

const props = withDefaults(defineProps<IComponentSearchProps>(), {
  modelValue: "",
  placeholder: "Placeholder",
  type: "text",
  width: "329px",
  height: "44px",
  clearable: false,
  disabled: false,
});

const isFocused = ref(false);

const customStyle = computed(() => {
  return {
    width: props.width,
    height: props.height,
    ...props.additionalStyle,
  };
});

const emit = defineEmits<IComponentInputEmits>();

const appendIconClick = () => {
  emit("appendIconClick");
};

const onInput = (e: Event) => {
  emit(
    "update:modelValue",
    (e.target as HTMLInputElement | HTMLTextAreaElement).value
  );
};

const onFocus = () => {
  isFocused.value = true;
  emit("focus");
};

const onBlur = () => {
  isFocused.value = false;
  emit("blur");
};

const clearValue = () => {
  emit("update:modelValue", "");
};
</script>

<template>
  <label>
    <div
      class="c-cu-search"
      :style="customStyle"
      :class="{ disabled: disabled }"
    >
      <div class="icon">
        <component :is="icon" :size="20" />
      </div>
      <div class="input">
        <input
          :value="modelValue"
          id="custom_search"
          v-maska
          :data-maska="mask"
          :type="type"
          :placeholder="placeholder"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
          :disabled="disabled"
        />

        <div v-if="appendIcon" class="append-icon" @click="appendIconClick">
          <component :is="appendIcon" :size="20" />
        </div>
      </div>
      <div v-if="clearable && modelValue" class="clear" @click="clearValue">
        <X :size="20" />
      </div>
    </div>
  </label>
</template>

<style scoped lang="scss">
.c-cu-search {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  background: var(--color-secondary-system-background);
  border: 1px solid var(--color-border);
  border-radius: 14px;
  padding-left: 12px;
  padding-right: 12px;

  &:focus-within,
  &:hover {
    border-color: var(--color-primary-disabled);
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.6;
  }

  .icon {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 24px;
    color: var(--color-label-secondary);
    background: transparent;
  }

  .input {
    width: 100%;

    input {
      background: var(--color-bg-secondary);
      border: none;
      font-size: 16px;
      line-height: 24px;
      height: 24px;
      width: 100%;
      color: var(--color-label);

      &:focus {
        outline: none;
      }
    }
  }

  .clear {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    color: var(--color-label-secondary);
    cursor: pointer;
  }
}
</style>
